import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils/cn";

/**
 * The row chips saying what on this resource awaits a second administrator.
 *
 * Mirrors the Django lists' four states (`ToolTable.render_title` /
 * `FinancialAidTable.render_title`). The lists open alphabetically, so these
 * chips are what marks a row awaiting validation in place.
 *
 * | state                                   | chip                  | reads as      |
 * |-----------------------------------------|-----------------------|---------------|
 * | someone else staged a link change       | "À valider"           | actionable    |
 * | you staged a link change                | "En attente"          | informational |
 * | someone else staged a display order     | "Ordre à valider"     | actionable    |
 * | you staged a display order              | "Ordre en attente"    | informational |
 *
 * The own-link chip is suppressed when the status pill already reads "En
 * attente" — a resource whose *initial* links are staged says it twice
 * otherwise, exactly the duplication the Django table guards against.
 */

function Chip({
  actionable,
  children,
}: {
  actionable: boolean;
  children: React.ReactNode;
}) {
  return (
    <span
      className={cn(
        "rounded-full px-2 py-0.5 text-[11px] font-semibold",
        actionable
          ? "bg-warning/20 text-foreground"
          : "bg-muted text-muted-foreground",
      )}
    >
      {children}
    </span>
  );
}

export function PendingValidationChips({
  linkChangePending = false,
  linkChangePendingMine = false,
  orderChangePending = false,
  orderChangePendingMine = false,
  status,
  namespace,
}: {
  linkChangePending?: boolean | null;
  linkChangePendingMine?: boolean | null;
  /**
   * Whether a staged display order moves *this* resource, and whether it is the
   * reader's own proposal. Both come from the server, which resolves them with
   * `pending_order_state_by_resource` — the same function Django's row chips
   * use. Derived per *link* instead, a proposal keyed on an action would chip
   * every resource linked to it, moved or not, and the row could then be
   * chipped yet hidden by the "en attente de validation" switch.
   */
  orderChangePending?: boolean | null;
  orderChangePendingMine?: boolean | null;
  /** The derived status code, so the own-link chip is not stated twice. */
  status?: string | null;
  /** "tools" or "financialAids" — the slice owning the linkOrder strings. */
  namespace: "tools" | "financialAids";
}) {
  const { t } = useTranslation();

  const orderPending = Boolean(orderChangePending);
  // "review" wins when both apply, because that is the one to act on — the
  // server applies that precedence when it resolves the state.
  const orderMine = orderPending && Boolean(orderChangePendingMine);

  // Someone else's link change is the one an administrator has to act on.
  const linkReview = Boolean(linkChangePending) && !linkChangePendingMine;
  const linkMine = Boolean(linkChangePendingMine) && status !== "pending";

  if (!linkReview && !linkMine && !orderPending) return null;

  return (
    <>
      {linkReview && (
        <Chip actionable>{t(`${namespace}.admin.linkChangeToValidate`)}</Chip>
      )}
      {linkMine && (
        <Chip actionable={false}>
          {t(`${namespace}.admin.linkChangePending`)}
        </Chip>
      )}
      {orderPending && (
        <Chip actionable={!orderMine}>
          {t(
            orderMine
              ? `${namespace}.admin.orderPending`
              : `${namespace}.admin.orderToValidate`,
          )}
        </Chip>
      )}
    </>
  );
}
